import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@tabipla/ui";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import type { MemberBalanceDto } from "@tabipla/shared";

export interface BalanceSummaryProps {
  balances: MemberBalanceDto[];
  /** Design variant: "avatar" (Waritto style) or "icon" (Tabitto style) */
  variant?: "avatar" | "icon";
  /** Custom avatar renderer. If provided, replaces the default avatar. */
  renderAvatar?: (member: {
    name: string;
    memberId: string;
    memberType: string;
    avatarEmoji?: string | null;
    avatarColor?: string | null;
  }) => React.ReactNode;
}

function getInitials(name: string): string {
  const parts = name.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function getAvatarColor(name: string): string {
  const colors = [
    "bg-blue-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-cyan-500",
    "bg-teal-500",
    "bg-orange-500",
    "bg-amber-500",
  ];
  const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
}

export function BalanceSummary({
  balances,
  variant = "avatar",
  renderAvatar,
}: BalanceSummaryProps) {
  const { t } = useTranslation();

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(amount);
  };

  const formatBalanceAmount = (amount: number) => {
    const formatted = formatAmount(Math.abs(amount));
    return amount >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  if (variant === "icon") {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("expenses.balances")}</CardTitle>
        </CardHeader>
        <CardContent>
          {balances.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              {t("expenses.noExpenses")}
            </p>
          ) : (
            <div className="space-y-3">
              {balances.map((balance) => (
                <div
                  key={`${balance.memberType}:${balance.memberId}`}
                  className={`rounded-lg border p-3 ${
                    balance.balance > 0
                      ? "border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/30"
                      : balance.balance < 0
                        ? "border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-950/30"
                        : ""
                  }`}
                >
                  {/* 1行目: 名前 + 残高 */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {balance.balance > 0 ? (
                        <TrendingUp className="h-4 w-4 shrink-0 text-green-500" />
                      ) : balance.balance < 0 ? (
                        <TrendingDown className="h-4 w-4 shrink-0 text-red-500" />
                      ) : (
                        <Minus className="h-4 w-4 shrink-0 text-gray-400" />
                      )}
                      <span className="font-medium">{balance.memberName}</span>
                    </div>
                    <span
                      className={`shrink-0 text-lg font-bold ${
                        balance.balance > 0
                          ? "text-green-600"
                          : balance.balance < 0
                            ? "text-red-600"
                            : "text-gray-500"
                      }`}
                    >
                      {balance.balance >= 0 ? "+" : ""}
                      {formatAmount(balance.balance)}
                    </span>
                  </div>
                  {/* 2行目: 詳細 */}
                  <p className="mt-1 text-right text-xs text-muted-foreground">
                    {t("expenses.paid")}: {formatAmount(balance.totalPaid)} / {t("expenses.owed")}:{" "}
                    {formatAmount(balance.totalOwed)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Default: avatar variant (Waritto style)
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{t("expenses.balances")}</CardTitle>
      </CardHeader>
      <CardContent>
        {balances.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            {t("expenses.noExpenses")}
          </p>
        ) : (
          <div className="space-y-2">
            {balances.map((balance) => {
              const isPositive = balance.balance > 0;
              const isNegative = balance.balance < 0;

              return (
                <div
                  key={`${balance.memberType}:${balance.memberId}`}
                  className={`flex items-center gap-3 rounded-xl p-3 transition-colors ${
                    isPositive
                      ? "bg-green-50 dark:bg-green-950/30"
                      : isNegative
                        ? "bg-red-50 dark:bg-red-950/30"
                        : "bg-gray-50 dark:bg-gray-800/50"
                  }`}
                >
                  {/* Avatar */}
                  {renderAvatar ? (
                    renderAvatar({
                      name: balance.memberName,
                      memberId: balance.memberId,
                      memberType: balance.memberType,
                      avatarEmoji: balance.avatarEmoji,
                      avatarColor: balance.avatarColor,
                    })
                  ) : balance.avatarEmoji || balance.avatarColor ? (
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-lg ${balance.avatarColor || "from-gray-300 to-gray-400"}`}
                    >
                      {balance.avatarEmoji || getInitials(balance.memberName)}
                    </div>
                  ) : (
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white ${getAvatarColor(balance.memberName)}`}
                    >
                      {getInitials(balance.memberName)}
                    </div>
                  )}

                  {/* Name & Details */}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{balance.memberName}</p>
                    <p className="text-xs text-muted-foreground">
                      {t("expenses.paid")}: {formatAmount(balance.totalPaid)} / {t("expenses.owed")}
                      : {formatAmount(balance.totalOwed)}
                    </p>
                  </div>

                  {/* Balance */}
                  <div
                    className={`shrink-0 text-right text-lg font-bold tabular-nums ${
                      isPositive
                        ? "text-green-600 dark:text-green-400"
                        : isNegative
                          ? "text-red-600 dark:text-red-400"
                          : "text-gray-500"
                    }`}
                  >
                    {formatBalanceAmount(balance.balance)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
